import React, { useEffect, useState } from "react";
import { motion } from "framer-motion";
import { Plus, Edit2, History as HistoryIcon, ChevronsUpDown, Check } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Command, CommandEmpty, CommandGroup, CommandInput, CommandItem, CommandList } from "@/components/ui/command";
import { toast } from "sonner";
import api from "@/lib/api";

const PLATFORMS = ["Deriv-Demo", "MT5", "DXTrade", "MatchTrader", "cTrader"];
const STATUSES = ["Pending", "Active", "Passed", "Failed", "Suspended", "Funded"];
const PHASES = ["Evaluation", "Funded", "Phase 1", "Phase 2"];

export const AdminAccounts = () => {
  const [accounts, setAccounts] = useState([]);
  const [traders, setTraders] = useState([]);
  const [orders, setOrders] = useState([]);
  const [assignOpen, setAssignOpen] = useState(false);
  const [traderPickerOpen, setTraderPickerOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [historyFor, setHistoryFor] = useState(null);
  const [history, setHistory] = useState([]);
  const [form, setForm] = useState({
    user_id: "", order_id: "", platform: "Deriv-Demo", broker: "Deriv.com Limited",
    login_id: "", password: "", server: "DerivDemo-Server", account_size: "10000",
    challenge_type: "1-Step", phase: "Evaluation",
    profit_target_pct: "15", daily_drawdown_pct: "5", max_drawdown_pct: "10",
    leverage: "1:1000", status: "Active",
  });
  const [busy, setBusy] = useState(false);

  const reload = () => {
    api.get("/admin/accounts").then((r) => setAccounts(r.data || []));
    api.get("/admin/traders").then((r) => setTraders(r.data || []));
    api.get("/admin/orders", { params: { status_filter: "paid" } }).then((r) => setOrders(r.data || []));
  };
  useEffect(() => { reload(); }, []);

  const submitAssign = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const payload = {
        ...form,
        account_size: parseFloat(form.account_size),
        profit_target_pct: parseFloat(form.profit_target_pct),
        daily_drawdown_pct: parseFloat(form.daily_drawdown_pct),
        max_drawdown_pct: parseFloat(form.max_drawdown_pct),
        order_id: form.order_id || null,
      };
      await api.post("/admin/accounts/assign", payload);
      toast.success("Account assigned. Credentials emailed to trader.");
      setAssignOpen(false);
      reload();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Assign failed");
    } finally {
      setBusy(false);
    }
  };

  const submitUpdate = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const updateFields = {};
      ["platform", "broker", "login_id", "password", "server", "status", "phase"].forEach((k) => {
        if (editing[k] !== undefined) updateFields[k] = editing[k];
      });
      ["current_balance", "current_equity", "current_pnl"].forEach((k) => {
        if (editing[k] !== undefined && editing[k] !== "") updateFields[k] = parseFloat(editing[k]);
      });
      await api.patch(`/admin/accounts/${editing.id}`, updateFields);
      toast.success("Account updated");
      setEditing(null);
      reload();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Update failed");
    } finally {
      setBusy(false);
    }
  };

  const openHistory = async (acc) => {
    setHistoryFor(acc);
    const { data } = await api.get(`/admin/accounts/${acc.id}/history`);
    setHistory(data || []);
  };

  // When trader selects an order, pre-fill challenge data
  const onOrderChange = (orderId) => {
    const order = orders.find((o) => o.id === orderId);
    setForm({
      ...form,
      order_id: orderId,
      user_id: order?.user_id || form.user_id,
      account_size: String(order?.account_size || form.account_size),
      challenge_type: order?.challenge_type || form.challenge_type,
    });
  };

  return (
    <div className="space-y-6" data-testid="admin-accounts-page">
      <div className="flex items-end justify-between flex-wrap gap-3">
        <div>
          <h1 className="font-display text-3xl font-bold">Trading Accounts</h1>
          <p className="text-[#8a8a8a] text-sm mt-1">Assign and manage trader accounts.</p>
        </div>
        <Dialog open={assignOpen} onOpenChange={setAssignOpen}>
          <DialogTrigger asChild>
            <Button className="bg-[#ff7a00] hover:bg-[#ff9a2f] text-[#050505] font-semibold" data-testid="admin-assign-button">
              <Plus size={16} className="mr-1.5" /> Assign Account
            </Button>
          </DialogTrigger>
          <DialogContent className="bg-[#0a0a0a] border-[#ff7a00]/30 text-[#f5f5f5] max-w-2xl max-h-[90vh] overflow-y-auto" data-testid="assign-dialog">
            <DialogHeader>
              <DialogTitle className="font-display">Assign Trading Account</DialogTitle>
            </DialogHeader>
            <form onSubmit={submitAssign} className="space-y-4">
              <div className="grid sm:grid-cols-2 gap-3">
                <div>
                  <Label className="text-xs uppercase tracking-wider text-[#cfcfcf]">From Paid Order (optional)</Label>
                  <Select value={form.order_id} onValueChange={onOrderChange}>
                    <SelectTrigger className="bg-[#050505] border-[#ff7a00]/20 h-11 mt-2" data-testid="assign-order-select"><SelectValue placeholder="—" /></SelectTrigger>
                    <SelectContent className="bg-[#0a0a0a] border-[#ff7a00]/30 text-[#f5f5f5]">
                      {orders.map((o) => <SelectItem key={o.id} value={o.id} className="text-[#f5f5f5]">{o.challenge_name} · ${o.account_size.toLocaleString()} · {o.user_id.slice(0,6)}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label className="text-xs uppercase tracking-wider text-[#cfcfcf]">Trader</Label>
                  <Popover open={traderPickerOpen} onOpenChange={setTraderPickerOpen}>
                    <PopoverTrigger asChild>
                      <Button
                        type="button"
                        variant="outline"
                        role="combobox"
                        aria-expanded={traderPickerOpen}
                        className="bg-[#050505] border-[#ff7a00]/20 h-11 mt-2 w-full justify-between font-normal text-[#f5f5f5] hover:bg-[#050505] hover:text-[#f5f5f5]"
                        data-testid="assign-trader-select"
                      >
                        <span className={form.user_id ? "" : "text-[#8a8a8a]"}>
                          {form.user_id
                            ? (traders.find((t) => t.id === form.user_id)?.email || "Select trader")
                            : "Search & select trader..."}
                        </span>
                        <ChevronsUpDown size={14} className="ml-2 shrink-0 opacity-60" />
                      </Button>
                    </PopoverTrigger>
                    <PopoverContent className="w-[--radix-popover-trigger-width] p-0 bg-[#0a0a0a] border-[#ff7a00]/30 text-[#f5f5f5]" align="start">
                      <Command className="bg-transparent">
                        <CommandInput
                          placeholder="Search by email or name..."
                          className="text-[#f5f5f5] placeholder:text-[#8a8a8a]"
                          data-testid="assign-trader-search"
                        />
                        <CommandList className="max-h-72">
                          <CommandEmpty className="py-6 text-center text-sm text-[#8a8a8a]">No trader found.</CommandEmpty>
                          <CommandGroup>
                            {traders.map((t) => {
                              const fullName = `${t.first_name || ""} ${t.last_name || ""}`.trim();
                              const label = fullName ? `${fullName} · ${t.email}` : t.email;
                              return (
                                <CommandItem
                                  key={t.id}
                                  value={`${t.email} ${fullName}`}
                                  onSelect={() => {
                                    setForm({ ...form, user_id: t.id });
                                    setTraderPickerOpen(false);
                                  }}
                                  className="text-[#f5f5f5] data-[selected=true]:bg-[#ff7a00]/10 data-[selected=true]:text-[#ff9a2f] cursor-pointer"
                                  data-testid={`assign-trader-option-${t.id}`}
                                >
                                  <Check size={14} className={`mr-2 ${form.user_id === t.id ? "opacity-100 text-[#ff7a00]" : "opacity-0"}`} />
                                  <span className="flex-1 truncate">{label}</span>
                                </CommandItem>
                              );
                            })}
                          </CommandGroup>
                        </CommandList>
                      </Command>
                    </PopoverContent>
                  </Popover>
                </div>
                <div>
                  <Label className="text-xs uppercase tracking-wider text-[#cfcfcf]">Platform</Label>
                  <Select value={form.platform} onValueChange={(v) => setForm({ ...form, platform: v })}>
                    <SelectTrigger className="bg-[#050505] border-[#ff7a00]/20 h-11 mt-2" data-testid="assign-platform-select"><SelectValue /></SelectTrigger>
                    <SelectContent className="bg-[#0a0a0a] border-[#ff7a00]/30 text-[#f5f5f5]">
                      {PLATFORMS.map((p) => <SelectItem key={p} value={p} className="text-[#f5f5f5]">{p}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label className="text-xs uppercase tracking-wider text-[#cfcfcf]">Broker / Company</Label>
                  <Input value={form.broker} onChange={(e) => setForm({ ...form, broker: e.target.value })} className="bg-[#050505] border-[#ff7a00]/20 h-11 mt-2" data-testid="assign-broker" />
                </div>
                <div>
                  <Label className="text-xs uppercase tracking-wider text-[#cfcfcf]">Login ID</Label>
                  <Input required value={form.login_id} onChange={(e) => setForm({ ...form, login_id: e.target.value })} className="bg-[#050505] border-[#ff7a00]/20 h-11 mt-2 font-mono" data-testid="assign-login-id" />
                </div>
                <div>
                  <Label className="text-xs uppercase tracking-wider text-[#cfcfcf]">Password</Label>
                  <Input required value={form.password} onChange={(e) => setForm({ ...form, password: e.target.value })} className="bg-[#050505] border-[#ff7a00]/20 h-11 mt-2 font-mono" data-testid="assign-password" />
                </div>
                <div>
                  <Label className="text-xs uppercase tracking-wider text-[#cfcfcf]">Server</Label>
                  <Input required value={form.server} onChange={(e) => setForm({ ...form, server: e.target.value })} className="bg-[#050505] border-[#ff7a00]/20 h-11 mt-2 font-mono" data-testid="assign-server" />
                </div>
                <div>
                  <Label className="text-xs uppercase tracking-wider text-[#cfcfcf]">Account Size (USD)</Label>
                  <Input type="number" required value={form.account_size} onChange={(e) => setForm({ ...form, account_size: e.target.value })} className="bg-[#050505] border-[#ff7a00]/20 h-11 mt-2" data-testid="assign-account-size" />
                </div>
                <div>
                  <Label className="text-xs uppercase tracking-wider text-[#cfcfcf]">Challenge Type</Label>
                  <Select value={form.challenge_type} onValueChange={(v) => setForm({ ...form, challenge_type: v })}>
                    <SelectTrigger className="bg-[#050505] border-[#ff7a00]/20 h-11 mt-2"><SelectValue /></SelectTrigger>
                    <SelectContent className="bg-[#0a0a0a] border-[#ff7a00]/30 text-[#f5f5f5]">
                      {["1-Step","Instant"].map((p) => <SelectItem key={p} value={p} className="text-[#f5f5f5]">{p}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label className="text-xs uppercase tracking-wider text-[#cfcfcf]">Phase</Label>
                  <Select value={form.phase} onValueChange={(v) => setForm({ ...form, phase: v })}>
                    <SelectTrigger className="bg-[#050505] border-[#ff7a00]/20 h-11 mt-2"><SelectValue /></SelectTrigger>
                    <SelectContent className="bg-[#0a0a0a] border-[#ff7a00]/30 text-[#f5f5f5]">
                      {PHASES.map((p) => <SelectItem key={p} value={p} className="text-[#f5f5f5]">{p}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label className="text-xs uppercase tracking-wider text-[#cfcfcf]">Profit Target %</Label>
                  <Input type="number" step="0.1" value={form.profit_target_pct} onChange={(e) => setForm({ ...form, profit_target_pct: e.target.value })} className="bg-[#050505] border-[#ff7a00]/20 h-11 mt-2" />
                </div>
                <div>
                  <Label className="text-xs uppercase tracking-wider text-[#cfcfcf]">Daily DD %</Label>
                  <Input type="number" step="0.1" value={form.daily_drawdown_pct} onChange={(e) => setForm({ ...form, daily_drawdown_pct: e.target.value })} className="bg-[#050505] border-[#ff7a00]/20 h-11 mt-2" />
                </div>
                <div>
                  <Label className="text-xs uppercase tracking-wider text-[#cfcfcf]">Max DD %</Label>
                  <Input type="number" step="0.1" value={form.max_drawdown_pct} onChange={(e) => setForm({ ...form, max_drawdown_pct: e.target.value })} className="bg-[#050505] border-[#ff7a00]/20 h-11 mt-2" />
                </div>
                <div>
                  <Label className="text-xs uppercase tracking-wider text-[#cfcfcf]">Leverage</Label>
                  <Input value={form.leverage} onChange={(e) => setForm({ ...form, leverage: e.target.value })} className="bg-[#050505] border-[#ff7a00]/20 h-11 mt-2" />
                </div>
                <div>
                  <Label className="text-xs uppercase tracking-wider text-[#cfcfcf]">Status</Label>
                  <Select value={form.status} onValueChange={(v) => setForm({ ...form, status: v })}>
                    <SelectTrigger className="bg-[#050505] border-[#ff7a00]/20 h-11 mt-2"><SelectValue /></SelectTrigger>
                    <SelectContent className="bg-[#0a0a0a] border-[#ff7a00]/30 text-[#f5f5f5]">
                      {STATUSES.map((s) => <SelectItem key={s} value={s} className="text-[#f5f5f5]">{s}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
              </div>
              <Button type="submit" disabled={busy || !form.user_id} className="w-full h-11 bg-[#ff7a00] hover:bg-[#ff9a2f] text-[#050505] font-semibold" data-testid="assign-submit">
                {busy ? "Assigning..." : "Assign & Email Credentials"}
              </Button>
            </form>
          </DialogContent>
        </Dialog>
      </div>

      <div className="glass rounded-xl overflow-x-auto">
        <table className="w-full text-sm">
          <thead>
            <tr className="text-[10px] tracking-widest text-[#8a8a8a] uppercase border-b border-[#ff7a00]/15">
              <th className="text-left p-4">Trader</th>
              <th className="text-left p-4">Platform</th>
              <th className="text-left p-4">Login</th>
              <th className="text-right p-4">Size</th>
              <th className="text-left p-4">Phase</th>
              <th className="text-left p-4">Status</th>
              <th className="text-right p-4">P&amp;L</th>
              <th className="text-right p-4">Actions</th>
            </tr>
          </thead>
          <tbody>
            {accounts.length === 0 && <tr><td colSpan={8} className="p-8 text-center text-[#8a8a8a]">No accounts assigned yet.</td></tr>}
            {accounts.map((a) => (
              <tr key={a.id} className="border-b border-[#ff7a00]/5 hover:bg-white/2" data-testid={`admin-account-row-${a.id}`}>
                <td className="p-4 text-[#cfcfcf]">{a.user_email}</td>
                <td className="p-4 text-[#f5f5f5]">{a.platform}</td>
                <td className="p-4 text-[#cfcfcf] font-mono text-xs">{a.login_id}</td>
                <td className="p-4 text-right font-display font-bold text-[#ff7a00]">${a.account_size.toLocaleString()}</td>
                <td className="p-4 text-[#cfcfcf]">{a.phase}</td>
                <td className="p-4">
                  <Badge className={`uppercase text-[10px] tracking-widest ${
                    a.status === "Funded" ? "bg-emerald-500/15 text-emerald-400 border-emerald-500/30" :
                    a.status === "Passed" ? "bg-[#ff7a00]/15 text-[#ff7a00] border-[#ff7a00]/30" :
                    a.status === "Failed" ? "bg-red-500/15 text-red-400 border-red-500/30" :
                    a.status === "Suspended" ? "bg-yellow-500/15 text-yellow-400 border-yellow-500/30" :
                    "bg-blue-500/10 text-blue-400 border-blue-500/30"
                  }`}>{a.status}</Badge>
                </td>
                <td className={`p-4 text-right ${(a.current_pnl || 0) >= 0 ? "text-emerald-400" : "text-red-400"}`}>
                  {(a.current_pnl || 0) >= 0 ? "+" : ""}${(a.current_pnl || 0).toLocaleString()}
                </td>
                <td className="p-4 text-right flex justify-end gap-1">
                  <Button size="icon" variant="ghost" onClick={() => setEditing({ ...a })} className="h-8 w-8 text-[#cfcfcf] hover:text-[#ff7a00]" data-testid={`account-edit-${a.id}`}><Edit2 size={14} /></Button>
                  <Button size="icon" variant="ghost" onClick={() => openHistory(a)} className="h-8 w-8 text-[#cfcfcf] hover:text-[#ff7a00]" data-testid={`account-history-${a.id}`}><HistoryIcon size={14} /></Button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* Edit dialog */}
      <Dialog open={!!editing} onOpenChange={(o) => !o && setEditing(null)}>
        <DialogContent className="bg-[#0a0a0a] border-[#ff7a00]/30 text-[#f5f5f5] max-w-xl" data-testid="edit-account-dialog">
          {editing && (
            <>
              <DialogHeader><DialogTitle className="font-display">Edit Account</DialogTitle></DialogHeader>
              <form onSubmit={submitUpdate} className="space-y-3">
                <div className="grid sm:grid-cols-2 gap-3">
                  <div>
                    <Label className="text-xs uppercase tracking-wider text-[#cfcfcf]">Login ID</Label>
                    <Input value={editing.login_id} onChange={(e) => setEditing({ ...editing, login_id: e.target.value })} className="bg-[#050505] border-[#ff7a00]/20 mt-2 font-mono" />
                  </div>
                  <div>
                    <Label className="text-xs uppercase tracking-wider text-[#cfcfcf]">Password</Label>
                    <Input value={editing.password} onChange={(e) => setEditing({ ...editing, password: e.target.value })} className="bg-[#050505] border-[#ff7a00]/20 mt-2 font-mono" />
                  </div>
                  <div>
                    <Label className="text-xs uppercase tracking-wider text-[#cfcfcf]">Server</Label>
                    <Input value={editing.server} onChange={(e) => setEditing({ ...editing, server: e.target.value })} className="bg-[#050505] border-[#ff7a00]/20 mt-2 font-mono" />
                  </div>
                  <div>
                    <Label className="text-xs uppercase tracking-wider text-[#cfcfcf]">Status</Label>
                    <Select value={editing.status} onValueChange={(v) => setEditing({ ...editing, status: v })}>
                      <SelectTrigger className="bg-[#050505] border-[#ff7a00]/20 mt-2"><SelectValue /></SelectTrigger>
                      <SelectContent className="bg-[#0a0a0a] border-[#ff7a00]/30 text-[#f5f5f5]">
                        {STATUSES.map((s) => <SelectItem key={s} value={s} className="text-[#f5f5f5]">{s}</SelectItem>)}
                      </SelectContent>
                    </Select>
                  </div>
                  <div>
                    <Label className="text-xs uppercase tracking-wider text-[#cfcfcf]">Phase</Label>
                    <Select value={editing.phase} onValueChange={(v) => setEditing({ ...editing, phase: v })}>
                      <SelectTrigger className="bg-[#050505] border-[#ff7a00]/20 mt-2"><SelectValue /></SelectTrigger>
                      <SelectContent className="bg-[#0a0a0a] border-[#ff7a00]/30 text-[#f5f5f5]">
                        {PHASES.map((p) => <SelectItem key={p} value={p} className="text-[#f5f5f5]">{p}</SelectItem>)}
                      </SelectContent>
                    </Select>
                  </div>
                  <div>
                    <Label className="text-xs uppercase tracking-wider text-[#cfcfcf]">Current Balance</Label>
                    <Input type="number" value={editing.current_balance ?? 0} onChange={(e) => setEditing({ ...editing, current_balance: e.target.value })} className="bg-[#050505] border-[#ff7a00]/20 mt-2" />
                  </div>
                  <div>
                    <Label className="text-xs uppercase tracking-wider text-[#cfcfcf]">Current Equity</Label>
                    <Input type="number" value={editing.current_equity ?? 0} onChange={(e) => setEditing({ ...editing, current_equity: e.target.value })} className="bg-[#050505] border-[#ff7a00]/20 mt-2" />
                  </div>
                  <div>
                    <Label className="text-xs uppercase tracking-wider text-[#cfcfcf]">Current P&amp;L</Label>
                    <Input type="number" value={editing.current_pnl ?? 0} onChange={(e) => setEditing({ ...editing, current_pnl: e.target.value })} className="bg-[#050505] border-[#ff7a00]/20 mt-2" />
                  </div>
                </div>
                <Button type="submit" disabled={busy} className="w-full bg-[#ff7a00] hover:bg-[#ff9a2f] text-[#050505] font-semibold" data-testid="edit-account-submit">
                  {busy ? "Saving..." : "Save Changes"}
                </Button>
              </form>
            </>
          )}
        </DialogContent>
      </Dialog>

      {/* History dialog */}
      <Dialog open={!!historyFor} onOpenChange={(o) => !o && setHistoryFor(null)}>
        <DialogContent className="bg-[#0a0a0a] border-[#ff7a00]/30 text-[#f5f5f5] max-w-xl" data-testid="history-dialog">
          {historyFor && (
            <>
              <DialogHeader><DialogTitle className="font-display">Assignment History</DialogTitle></DialogHeader>
              <div className="space-y-2 max-h-96 overflow-y-auto">
                {history.length === 0 && <div className="text-sm text-[#8a8a8a]">No history.</div>}
                {history.map((h) => (
                  <div key={h.id} className="text-sm p-3 rounded-lg bg-[#050505] border border-[#ff7a00]/10">
                    <div className="flex justify-between">
                      <span className="text-[#ff7a00] font-semibold uppercase text-xs tracking-widest">{h.action}</span>
                      <span className="text-[#8a8a8a] text-xs">{new Date(h.timestamp).toLocaleString()}</span>
                    </div>
                    <pre className="text-[10px] text-[#8a8a8a] mt-2 overflow-x-auto">{JSON.stringify(h.details, null, 2)}</pre>
                  </div>
                ))}
              </div>
            </>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
};
